import { useState } from 'react';
import type { AgentDefinitionResponse, AgentPageResponse } from '@erp/rpc';
import { useGetAgentStartReadiness } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  useAgentOpenTaskCounts,
  useListAgentTasks,
} from 'src/api/rpc/hooks/tasks/useTasks';
import { isOrchestratedAgent } from '../../agent-monitor/components/isOrchestratedAgent';
import {
  AGENT_PAGE_TASKS_ID,
  openTaskPreview,
  summarizeRuns,
  workPaneForAction,
  type AgentPageWorkPane,
} from '../agentPageRuns';
import { useAgentPageRuns } from '../useAgentPageRuns';
import { AgentPageHeader } from './AgentPageHeader';
import { AgentPageStats } from './AgentPageStats';
import { AgentPageLiveTask } from './AgentPageCurrentTask';
import { AgentPageWorkSection } from './AgentPageWorkSection';
import { AgentSchedulesSection } from './AgentSchedulesSection';
import { AgentStartReadinessNotice } from './AgentStartReadinessNotice';
import { AgentConfigWarningsNotice } from '../../agents/components/AgentConfigWarningsNotice';

/**
 * The page body once the page and its agent are loaded: header with Run /
 * Stop, the stat strip, the agent's run schedules, then history + the
 * selected run (and, for queue-driven agents, the task list in the same
 * tabbed section).
 */
export function AgentPageBody({
  page,
  definition,
}: {
  page: AgentPageResponse;
  definition: AgentDefinitionResponse;
}) {
  const [workPane, setWorkPane] = useState<AgentPageWorkPane>('runs');
  const { recentRuns, latestAttemptAt, history } = useAgentPageRuns(
    page.agentDefinitionId,
  );
  // The newest run that did (or is doing) work; skipped checks never count.
  const latestRun = recentRuns[0] ?? null;
  // Newest-first, but a run still going is the one the page should explain even
  // if a later row somehow sorts above it.
  const liveRun = recentRuns.find((run) => run.status === 'running') ?? null;

  // Task-management agents work a queue; only they get a Tasks tab.
  const orchestrated = isOrchestratedAgent(definition.config);
  const taskAgentId = orchestrated ? page.agentDefinitionId : undefined;
  const { grouped } = useListAgentTasks(taskAgentId);
  const { data: openTaskCounts } = useAgentOpenTaskCounts(taskAgentId);
  const openTasks = orchestrated
    ? openTaskPreview(grouped, page.agentDefinitionId, openTaskCounts)
    : null;
  // Only a task-managed agent can be stuck waiting on the coordinator.
  const { data: startReadiness } = useGetAgentStartReadiness(
    page.agentDefinitionId,
    { enabled: orchestrated },
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      <AgentPageHeader
        page={page}
        latestRun={latestRun}
        orchestrated={orchestrated}
      />

      <AgentConfigWarningsNotice
        warnings={definition.configWarnings}
        agentDefinitionId={page.agentDefinitionId}
      />

      <AgentStartReadinessNotice
        readiness={startReadiness}
        agentDefinitionId={page.agentDefinitionId}
        isRunning={liveRun != null}
      />

      {liveRun && <AgentPageLiveTask run={liveRun} />}

      <AgentPageStats
        latestRun={latestRun}
        latestAttemptAt={latestAttemptAt}
        stats={summarizeRuns(recentRuns)}
        openTasks={openTasks}
        onOpenTasksClick={
          orchestrated ? () => openAgentPageTasks(setWorkPane) : undefined
        }
      />

      <AgentSchedulesSection agentDefinitionId={page.agentDefinitionId} />

      <AgentPageWorkSection
        pane={workPane}
        onPaneChange={setWorkPane}
        showTasks={orchestrated}
        openTaskCount={openTasks?.count ?? 0}
        latestRun={latestRun}
        history={history}
        agentDefinitionId={page.agentDefinitionId}
        agentName={definition.name}
      />
    </div>
  );
}

function openAgentPageTasks(setWorkPane: (pane: AgentPageWorkPane) => void) {
  setWorkPane(workPaneForAction('open-tasks'));
  document.getElementById(AGENT_PAGE_TASKS_ID)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}
